import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import moduleRealtimeService from '../../../services/moduleRealtimeService';

const BudgetSummary = ({ data, modules, onBudgetUpdate }) => {
  const { projectId } = useParams();

  // Real-time subscription for module and budget changes
  useEffect(() => {
    if (!projectId) return;

    const moduleChannel = moduleRealtimeService?.subscribeToModuleChanges(
      projectId,
      (payload) => {
        console.log('Budget-related module change detected:', payload?.event);
        
        // Trigger budget recalculation when modules change
        if (onBudgetUpdate) {
          onBudgetUpdate();
        }
      }
    );

    const budgetChannel = moduleRealtimeService?.subscribeToBudgetChanges(
      projectId,
      (payload) => {
        console.log('Budget change detected:', payload?.event);
        
        // Trigger budget refresh when budget data changes
        if (onBudgetUpdate) {
          onBudgetUpdate();
        }
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      moduleRealtimeService?.unsubscribeAll([moduleChannel, budgetChannel]);
    };
  }, [projectId, onBudgetUpdate]);

  // Calculate total budget from modules: sum of (production_value_usd * quantity)
  const calculateTotalBudgetFromModules = () => {
    if (!modules || modules?.length === 0) return 0;
    
    return modules?.reduce((total, module) => {
      const productionValue = parseFloat(module?.productionValueUsd) || 0;
      const quantity = parseInt(module?.quantity) || 0;
      return total + (productionValue * quantity);
    }, 0);
  };

  const totalBudgetFromModules = calculateTotalBudgetFromModules();
  
  // Use calculated total budget from modules, fallback to data if modules not available
  const totalBudget = totalBudgetFromModules > 0 ? totalBudgetFromModules : (data?.totalBudget || 0);

  if (!data && (!modules || modules?.length === 0)) {
    return (
      <div className="bg-card rounded-xl border border-border p-6">
        <h2 className="text-lg font-semibold text-foreground mb-4">Budget Summary</h2>
        <p className="text-muted-foreground text-sm">No budget data available</p>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: data?.currency || 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const spentPercentage = totalBudget > 0 ? (data?.spentBudget / totalBudget) * 100 : 0;
  const remainingBudget = totalBudget - (data?.spentBudget || 0);
  const budgetVariance = totalBudget - (data?.allocatedBudget || 0);
  const variancePercentage = totalBudget > 0 ? (budgetVariance / totalBudget) * 100 : 0;

  return (
    <div className="bg-card rounded-xl border border-border p-4 h-full">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-foreground">Budget Summary</h2>
        <Icon name="DollarSign" size={18} color="var(--color-primary)" />
      </div>

      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Allocated</span>
            <span className="text-base font-bold text-foreground">{formatCurrency(totalBudget)}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Spent</span>
            <span className="text-sm font-semibold text-foreground">{formatCurrency(data?.spentBudget)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Remaining</span>
            <span className="text-sm font-semibold text-success">{formatCurrency(remainingBudget)}</span>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-muted-foreground">Budget Utilization</span>
            <span className="text-xs font-medium text-foreground">{spentPercentage?.toFixed(1)}%</span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(spentPercentage, 100)}%`,
                backgroundColor: spentPercentage > 100 ? 'var(--color-error)' : spentPercentage > 90 ? 'var(--color-warning)' : 'var(--color-success)'
              }}
            />
          </div>
        </div>

        <div className="border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Variance</span>
            <div className="flex items-center gap-1">
              <Icon
                name={budgetVariance >= 0 ? 'TrendingUp' : 'TrendingDown'}
                size={12}
                color={budgetVariance >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
              />
              <span
                className="text-xs font-semibold"
                style={{ color: budgetVariance >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}
              >
                {formatCurrency(Math.abs(budgetVariance))} ({Math.abs(variancePercentage)?.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetSummary;